import React, { useEffect, useRef } from 'react';

import { InfiniteScrollIndicator } from '@/components/table/InfiniteScrollIndicator';
import { Spinner } from '@/components/ui/spinner';
import { useOnScreen } from '@/hooks/useOnScreen';

import { FlatRow, flattenTree } from './flattenTree';
import { SitemapTreeRow } from './SitemapTreeRow';
import { SitemapRowHandlers, WebpageTreeNode } from './types';
import { SIGNAL_DEFS } from './webpageSignals';

interface SitemapTableProps {
  /** Header for the first (name) column: "Path" for the tree, "URL" externally. */
  firstColumnLabel: string;
  rows: FlatRow[];
  expanded: Set<string>;
  onToggle: (path: string) => void;
  handlers: SitemapRowHandlers;
}

const SitemapTable: React.FC<SitemapTableProps> = ({
  firstColumnLabel,
  rows,
  expanded,
  onToggle,
  handlers,
}) => (
  <table className="w-full border-collapse text-sm">
    <thead className="bg-chariot-background-primary sticky top-0 z-[1]">
      <tr className="border-chariot-stroke border-b">
        <th className="text-chariot-text-secondary px-2 py-2 text-left text-sm font-semibold">
          {firstColumnLabel}
        </th>
        {SIGNAL_DEFS.map(def => (
          <th
            key={def.key}
            className="text-chariot-text-secondary px-2 py-2 text-center text-sm font-semibold whitespace-nowrap"
          >
            {def.label}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map(({ node, level, isLast, pathFlags }) => (
        <SitemapTreeRow
          key={node.path}
          node={node}
          level={level}
          isLast={isLast}
          pathFlags={pathFlags}
          isExpanded={node.children ? expanded.has(node.path) : false}
          onToggle={onToggle}
          handlers={handlers}
        />
      ))}
    </tbody>
  </table>
);

interface SitemapTreeProps {
  internalNodes: WebpageTreeNode[];
  /** Off-host pages, rendered as a flat table (no path hierarchy). */
  externalNodes: WebpageTreeNode[];
  expanded: Set<string>;
  onToggle: (path: string) => void;
  handlers: SitemapRowHandlers;
  status: 'error' | 'pending' | 'success';
  internalCount: number;
  externalCount: number;
  /** Total loaded webpages, drives the infinite-scroll indicator threshold. */
  itemCount: number;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  hasActiveFilters: boolean;
  /** A non-empty (debounced) search term is applied to the query. */
  hasActiveSearch: boolean;
}

/**
 * The scrollable sitemap: an expandable table for the internal path tree plus a
 * flat table for external URLs, with infinite scroll on its own container.
 */
export const SitemapTree: React.FC<SitemapTreeProps> = ({
  internalNodes,
  externalNodes,
  expanded,
  onToggle,
  handlers,
  status,
  internalCount,
  externalCount,
  itemCount,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  hasActiveFilters,
  hasActiveSearch,
}) => {
  // Load more when a bottom sentinel scrolls into view. IntersectionObserver
  // (via useOnScreen) fires regardless of WHICH ancestor actually scrolls, so
  // this works even when the drawer — not this container — is the scroller.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const sentinelVisible = useOnScreen(sentinelRef);
  useEffect(() => {
    if (sentinelVisible && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [sentinelVisible, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const internalRows = flattenTree(internalNodes, expanded);
  const externalRows = flattenTree(externalNodes, expanded);
  const emptyMessage = hasActiveFilters
    ? 'No pages match the active filters.'
    : hasActiveSearch
      ? 'No pages match your search.'
      : 'No Pages Found';

  return (
    <div
      className="min-h-0 flex-1 overflow-auto"
      data-testid="sitemap-tree-scroll"
    >
      <section className="flex flex-col gap-2">
        <h3 className="text-chariot-text-secondary text-sm font-semibold">
          Sitemap ({internalCount})
        </h3>
        {status === 'pending' ? (
          <Spinner centered />
        ) : status === 'error' ? (
          <p className="text-chariot-text-error px-2 py-4 text-sm">
            Failed to load the sitemap.
          </p>
        ) : internalRows.length === 0 ? (
          <p className="text-chariot-text-secondary px-2 py-4 text-sm">
            {emptyMessage}
          </p>
        ) : (
          <SitemapTable
            firstColumnLabel="Path"
            rows={internalRows}
            expanded={expanded}
            onToggle={onToggle}
            handlers={handlers}
          />
        )}
      </section>

      {externalCount > 0 && (
        <section className="mt-4 flex flex-col gap-2">
          <h3 className="text-chariot-text-secondary text-sm font-semibold">
            External URLs ({externalCount})
          </h3>
          <SitemapTable
            firstColumnLabel="URL"
            rows={externalRows}
            expanded={expanded}
            onToggle={onToggle}
            handlers={handlers}
          />
        </section>
      )}

      {/* Sentinel observed for infinite scroll (see useOnScreen above). */}
      <div ref={sentinelRef} className="h-px w-full" aria-hidden />

      <InfiniteScrollIndicator
        variant="sticky"
        isLoading={isFetchingNextPage}
        hasMore={hasNextPage === true}
        itemCount={itemCount}
      />
    </div>
  );
};
